// src/pages/motolivraison/motolivraison-dashboard.page.jsx
//
// Dashboard du module MotoLivraison : stats globales avec sélecteur de période.

import React, { useEffect, useState, useCallback } from 'react';
import {
  Card, Row, Col, Statistic, message, Breadcrumb, Spin, Tag, Space, Button
} from 'antd';
import {
  ShopOutlined, CarOutlined, ContainerOutlined, DollarOutlined,
  RightOutlined, ClockCircleOutlined, CheckCircleOutlined, PercentageOutlined,
  AppstoreOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { motolivraisonService } from '../../common/services';

const PERIODES = [
  { value: 'daily', label: "Aujourd'hui" },
  { value: 'weekly', label: '7 jours' },
  { value: 'monthly', label: '30 jours' },
  { value: 'all', label: 'Tout' },
];

const MotolivraisonDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [periode, setPeriode] = useState('monthly');

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const data = await motolivraisonService.getStats(periode);
      setStats(data);
    } catch (e) {
      message.error('Erreur lors du chargement des statistiques');
    } finally {
      setLoading(false);
    }
  }, [periode]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  if (loading || !stats) {
    return <div style={{ textAlign: 'center', padding: 60 }}><Spin size="large" /></div>;
  }

  const { commerces, livreurs, commandes, revenus, reversements, catalogue } = stats;

  return (
    <div className="content-wrapper">
      <div className="container-xxl flex-grow-1" style={{ padding: '8px 24px' }}>
        <Breadcrumb separator={<RightOutlined />} style={{ marginBottom: 12 }}>
          <Breadcrumb.Item><Link to="/dashboard">Accueil</Link></Breadcrumb.Item>
          <Breadcrumb.Item>MotoLivraison</Breadcrumb.Item>
          <Breadcrumb.Item>Dashboard</Breadcrumb.Item>
        </Breadcrumb>

        {/* Sélecteur période */}
        <Card style={{ marginBottom: 16 }}>
          <Space wrap>
            <span style={{ fontWeight: 600, marginRight: 8 }}>Période :</span>
            {PERIODES.map(p => (
              <Button
                key={p.value}
                type={periode === p.value ? 'primary' : 'default'}
                onClick={() => setPeriode(p.value)}
              >
                {p.label}
              </Button>
            ))}
          </Space>
        </Card>

        {/* Carte revenus principale */}
        <Card
          style={{
            marginBottom: 16,
            background: 'linear-gradient(135deg, #fa8c16 0%, #ffc069 100%)',
            border: 'none',
          }}
        >
          <Row gutter={16}>
            <Col xs={12} md={6}>
              <Statistic
                title={<span style={{ color: 'rgba(255,255,255,0.85)' }}>Revenus TTC total</span>}
                value={revenus?.montantTTCTotal || 0}
                suffix=" F"
                valueStyle={{ color: 'white', fontSize: 24, fontWeight: 'bold' }}
                prefix={<DollarOutlined />}
              />
            </Col>
            <Col xs={12} md={6}>
              <Statistic
                title={<span style={{ color: 'rgba(255,255,255,0.85)' }}>Commission plateforme</span>}
                value={revenus?.commissionPlateforme || 0}
                suffix=" F"
                valueStyle={{ color: 'white', fontSize: 24, fontWeight: 'bold' }}
                prefix={<PercentageOutlined />}
              />
            </Col>
            <Col xs={12} md={6}>
              <Statistic
                title={<span style={{ color: 'rgba(255,255,255,0.85)' }}>Articles commerçants</span>}
                value={revenus?.montantArticles || 0}
                suffix=" F"
                valueStyle={{ color: 'white', fontSize: 24, fontWeight: 'bold' }}
              />
            </Col>
            <Col xs={12} md={6}>
              <Statistic
                title={<span style={{ color: 'rgba(255,255,255,0.85)' }}>Total commandes</span>}
                value={commandes?.total || 0}
                valueStyle={{ color: 'white', fontSize: 24, fontWeight: 'bold' }}
                prefix={<ContainerOutlined />}
              />
            </Col>
          </Row>
        </Card>

        {/* Commerces */}
        <Card title={<><ShopOutlined /> Commerces</>} style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col xs={12} sm={6}><Statistic title="Total" value={commerces?.total || 0} /></Col>
            <Col xs={12} sm={6}><Statistic title="Approuvés" value={commerces?.approved || 0} valueStyle={{ color: '#52c41a' }} /></Col>
            <Col xs={12} sm={6}><Statistic title="En attente" value={commerces?.pending || 0} valueStyle={{ color: '#fa8c16' }} /></Col>
            <Col xs={12} sm={6}><Statistic title="Suspendus" value={commerces?.suspended || 0} valueStyle={{ color: '#f5222d' }} /></Col>
          </Row>
        </Card>

        {/* Livreurs */}
        <Card title={<><CarOutlined /> Livreurs</>} style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col xs={12} sm={6}><Statistic title="Total" value={livreurs?.total || 0} /></Col>
            <Col xs={12} sm={6}><Statistic title="Approuvés" value={livreurs?.approved || 0} valueStyle={{ color: '#52c41a' }} /></Col>
            <Col xs={12} sm={6}><Statistic title="En attente" value={livreurs?.pending || 0} valueStyle={{ color: '#fa8c16' }} /></Col>
            <Col xs={12} sm={6}><Statistic title="🟢 Actifs maintenant" value={livreurs?.actifsMaintenant || 0} valueStyle={{ color: '#1890ff' }} /></Col>
          </Row>
        </Card>

        {/* Commandes par statut */}
        <Card title={<><ContainerOutlined /> Commandes par statut</>} style={{ marginBottom: 16 }}>
          <Space wrap size={12}>
            {Object.entries(commandes?.parStatut || {}).map(([statut, info]) => (
              <Tag key={statut} color="blue" style={{ padding: '4px 12px', fontSize: 13 }}>
                <strong>{statut}</strong> : {info.count} ({info.montantTTC} F)
              </Tag>
            ))}
          </Space>
        </Card>

        {/* Commandes par mode */}
        <Card title="Commandes par mode" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col xs={12}>
              <Statistic
                title="Mode App"
                value={commandes?.parMode?.app?.count || 0}
                suffix={` (${commandes?.parMode?.app?.montantTTC || 0} F)`}
              />
            </Col>
            <Col xs={12}>
              <Statistic
                title="Mode Téléphone"
                value={commandes?.parMode?.telephone?.count || 0}
                suffix={` (${commandes?.parMode?.telephone?.montantTTC || 0} F)`}
              />
            </Col>
          </Row>
        </Card>

        {/* Reversements */}
        <Card title={<><DollarOutlined /> Reversements</>} style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col xs={12} sm={6}><Statistic title="En attente" value={reversements?.pendingCount || 0} prefix={<ClockCircleOutlined />} valueStyle={{ color: '#fa8c16' }} /></Col>
            <Col xs={12} sm={6}><Statistic title="Payés (période)" value={reversements?.paidCount || 0} prefix={<CheckCircleOutlined />} valueStyle={{ color: '#52c41a' }} /></Col>
            <Col xs={12} sm={6}><Statistic title="Cmd reversées" value={reversements?.commandesReversees || 0} /></Col>
            <Col xs={12} sm={6}><Statistic title="Montant reversé" value={reversements?.montantNetReverse || 0} suffix=" F" valueStyle={{ color: '#52c41a' }} /></Col>
          </Row>
        </Card>

        {/* Catalogue */}
        <Card title={<><AppstoreOutlined /> Catalogue</>}>
          <Row gutter={16}>
            <Col xs={8}><Statistic title="Catégories actives" value={catalogue?.categoriesActives || 0} /></Col>
            <Col xs={8}><Statistic title="Articles total" value={catalogue?.articlesTotal || 0} /></Col>
            <Col xs={8}><Statistic title="Articles actifs" value={catalogue?.articlesActifs || 0} valueStyle={{ color: '#52c41a' }} /></Col>
          </Row>
        </Card>
      </div>
    </div>
  );
};

export default MotolivraisonDashboard;
